import { useQuery } from "@tanstack/react-query";
import { resolveRecordScopeFromServices } from "../domain/record-scope";
import type { CoachingRole } from "../domain/roles";
import type { AppRouteDefinition } from "../routes/route-types";
import { getCoachingServices } from "../services/coaching-service-provider";
import { useWalkReadinessHold } from "../lib/walk-readiness";
import { coachingQueryKeys } from "./coaching-query-keys";
export const liveRecordScopeQueryPolicy = {
  retry: false,
  staleTime: 0,
  refetchOnMount: "always" as const,
  refetchOnWindowFocus: true
};

export function useLiveRecordScopeDecision({
  enabled,
  role,
  route,
  routePath
}: {
  enabled: boolean;
  role: CoachingRole;
  route: AppRouteDefinition | null;
  routePath: string;
}) {
  const scopeKind = route?.scopeKind ?? "none";
  const recordId = route ? getRecordId(route, routePath) : null;

  const query = useQuery({
    queryKey: coachingQueryKeys.recordScope(role, scopeKind, recordId ?? "missing"),
    queryFn: () => {
      if (!route) throw new Error("Cannot resolve record scope without a route.");
      return resolveRecordScopeFromServices(role, route, routePath, getCoachingServices());
    },
    enabled: enabled && Boolean(route && scopeKind !== "none"),
    ...liveRecordScopeQueryPolicy
  });
  useWalkReadinessHold(query.isLoading || (query.isPending && query.isFetching));
  return query;
}

function getRecordId(route: AppRouteDefinition, routePath: string) {
  if (!route.scopeParam) return null;
  const templateParts = route.path.split("/").filter(Boolean);
  const pathParts = routePath.split("?")[0].split("/").filter(Boolean);
  const paramIndex = templateParts.findIndex((part) => part === `:${route.scopeParam}`);
  return paramIndex >= 0 ? pathParts[paramIndex] ?? null : null;
}
